import React, { useState, useEffect } from 'react';

interface UserData {
  phone: string;
  countryCode: string;
  testType: 'english' | 'math';
  testLanguage: 'english' | 'arabic';
  studyLocation: string;
  nationality: string;
  studyField: string;
  grade: string;
  schoolType: 'private' | 'government';
  schoolName: string;
}

interface QuizAnswer {
  questionId: number;
  selectedOption: number;
  isCorrect: boolean;
}

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  section?: string;
}

interface QuizStepProps {
  userData: UserData;
  timeLeft: number;
  formatTime: (seconds: number) => string;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  quizAnswers: QuizAnswer[];
  setQuizAnswers: React.Dispatch<React.SetStateAction<QuizAnswer[]>>;
  onSubmit: () => void;
  isRTL: boolean;
}

const QuizStep: React.FC<QuizStepProps> = ({
  userData,
  timeLeft,
  formatTime,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  quizAnswers,
  setQuizAnswers,
  onSubmit,
  isRTL
}) => {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [currentSection, setCurrentSection] = useState<string>('');

  // Generate questions based on test type
  const generateQuestions = (): Question[] => {
    if (userData.testType === 'math') {
      return generateMathQuestions();
    } else {
      return generateEnglishQuestions();
    }
  };

  const generateMathQuestions = (): Question[] => {
    const questions: Question[] = [];
    const isArabic = userData.testLanguage === 'arabic';
    
    // Sample math questions
    for (let i = 1; i <= 20; i++) {
      questions.push({
        id: i,
        question: isArabic ? 
          `ما هو ناتج ${i + 5} + ${i + 3}؟` : 
          `What is ${i + 5} + ${i + 3}?`,
        options: [
          `${i + 5 + i + 3}`,
          `${i + 5 + i + 3 + 1}`,
          `${i + 5 + i + 3 - 1}`,
          `${i + 5 + i + 3 + 2}`
        ],
        correctAnswer: 0
      });
    }
    return questions;
  };

  const generateEnglishQuestions = (): Question[] => {
    const questions: Question[] = [];
    
    // Grammar section (8 questions)
    for (let i = 1; i <= 8; i++) {
      questions.push({
        id: i,
        section: 'Grammar',
        question: `Choose the correct sentence:`,
        options: [
          'She don\'t like coffee',
          'She doesn\'t like coffee',
          'She not like coffee',
          'She doesn\'t likes coffee'
        ],
        correctAnswer: 1
      });
    }
    
    // Vocabulary section (8 questions)
    for (let i = 9; i <= 16; i++) {
      questions.push({
        id: i,
        section: 'Vocabulary',
        question: `What is the synonym of "happy"?`,
        options: ['Sad', 'Joyful', 'Angry', 'Tired'],
        correctAnswer: 1
      });
    }
    
    // Reading section (4 questions)
    for (let i = 17; i <= 20; i++) {
      questions.push({
        id: i,
        section: 'Reading',
        question: `According to the passage, what is the main idea?`,
        options: [
          'Education is important',
          'Technology is advancing',
          'Climate change is real',
          'Health matters most'
        ],
        correctAnswer: 0
      });
    }
    
    return questions;
  };

  const [questions] = useState<Question[]>(generateQuestions());

  // Update current section for English tests
  useEffect(() => {
    if (userData.testType === 'english' && questions[currentQuestionIndex]) {
      setCurrentSection(questions[currentQuestionIndex].section || '');
    }
  }, [currentQuestionIndex, questions, userData.testType]);

  // Load existing answer when question changes
  useEffect(() => {
    const existingAnswer = quizAnswers.find(
      answer => answer.questionId === questions[currentQuestionIndex]?.id
    );
    setSelectedOption(existingAnswer ? existingAnswer.selectedOption : null);
  }, [currentQuestionIndex, quizAnswers, questions]);

  const handleOptionSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };

  const handleNext = () => {
    if (selectedOption !== null) {
      // Save answer
      const newAnswer: QuizAnswer = {
        questionId: questions[currentQuestionIndex].id,
        selectedOption,
        isCorrect: selectedOption === questions[currentQuestionIndex].correctAnswer
      };

      setQuizAnswers(prev => {
        const filtered = prev.filter(answer => answer.questionId !== questions[currentQuestionIndex].id);
        return [...filtered, newAnswer];
      });

      // Move to next question or submit
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        onSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const getQuizProgress = () => {
    return ((currentQuestionIndex + 1) / questions.length) * 100;
  };

  const getSectionInfo = () => {
    if (userData.testType === 'math') {
      return isRTL ? 'قسم الرياضيات' : 'Mathematics Section';
    } else {
      const sectionNames = {
        'Grammar': isRTL ? 'قسم القواعد' : 'Grammar Section',
        'Vocabulary': isRTL ? 'قسم المفردات' : 'Vocabulary Section',
        'Reading': isRTL ? 'قسم القراءة' : 'Reading Section'
      };
      return sectionNames[currentSection as keyof typeof sectionNames] || '';
    }
  };

  if (!questions[currentQuestionIndex]) {
    return <div>Loading...</div>;
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="d-flex flex-column h-100">
      {/* Timer and Section Info */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body p-3">
          <div className="row align-items-center">
            <div className="col-6">
              <div className="d-flex align-items-center">
                <i className="bi bi-clock text-danger me-2"></i>
                <span className="fw-bold text-danger fs-5">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
            <div className="col-6 text-end">
              <small className="text-muted">
                {getSectionInfo()}
              </small>
            </div>
          </div>
          
          {/* Quiz Progress */}
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                {isRTL ? 'التقدم' : 'Progress'}
              </small>
              <small className="text-muted">
                {currentQuestionIndex + 1} / {questions.length}
              </small>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div 
                className="progress-bar bg-success" 
                style={{ width: `${getQuizProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="card border-0 shadow-sm flex-grow-1">
        <div className="card-body p-4">
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="badge bg-primary rounded-pill">
                {isRTL ? 'سؤال' : 'Question'} {currentQuestionIndex + 1}
              </span>
              {userData.testType === 'english' && (
                <span className="badge bg-secondary rounded-pill">
                  {currentSection}
                </span>
              )}
            </div>
            
            <h3 className="fw-bold fs-5 mb-3">
              {currentQuestion.question}
            </h3>
          </div>

          {/* Options */}
          <div className="d-grid gap-2 mb-4">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                className={`btn d-block w-100 text-start rounded-pill ${
                  selectedOption === index 
                    ? 'btn-primary text-white' 
                    : 'btn-outline-secondary'
                }`}
                onClick={() => handleOptionSelect(index)}
              >
                <span className="me-3 fw-bold">
                  {String.fromCharCode(65 + index)}.
                </span>
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="card border-0 shadow-sm mt-3">
        <div className="card-body p-3">
          <div className="row g-2">
            <div className="col-6">
              <button 
                className="btn btn-outline-secondary w-100 rounded-pill"
                onClick={handleBack}
                disabled={currentQuestionIndex === 0}
              >
                <i className={`bi ${isRTL ? 'bi-arrow-right' : 'bi-arrow-left'} me-2`}></i>
                {isRTL ? 'السابق' : 'Back'}
              </button>
            </div>
            <div className="col-6">
              <button 
                className="btn btn-primary w-100 rounded-pill"
                onClick={handleNext}
                disabled={selectedOption === null}
              >
                {currentQuestionIndex === questions.length - 1 
                  ? (isRTL ? 'إرسال الاختبار' : 'Submit Test')
                  : (isRTL ? 'التالي' : 'Next')
                }
                <i className={`bi ${isRTL ? 'bi-arrow-left' : 'bi-arrow-right'} ms-2`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizStep;
